"use client";
import { createContext, useState, useContext } from "react";

type CursorState = "default" | "hover" | "click" | "hidden" | "list" | null;
type CursorCoordinates = { x: number; y: number };

type AppContextType = {
    cursorState: CursorState;
    cursorText: string;
    cursorCoordinates: { x: number; y: number };
    isBannerHidden: boolean;
    bannerBgColor: string;
    isInView: boolean;
    setIsBannerHidden: (isHidden: boolean) => void;
    setCursorState: (state: CursorState) => void;
    setCursorText: (text: string) => void;
    setCursorCoordinates: (coordinates: { x: number; y: number }) => void;
    setBannerBgColor: (color: string) => void;
    setIsInView: (isInView: boolean) => void;
};

const AppContext = createContext<AppContextType>({
    cursorState: "default",
    cursorText: "",
    cursorCoordinates: { x: 0, y: 0 },
    isBannerHidden: false,
    bannerBgColor: "",
    isInView: false,
    setCursorText: () => {}, // noop
    setCursorState: () => {}, // noop
    setCursorCoordinates: () => {}, // noop
    setIsBannerHidden: () => {},
    setBannerBgColor: () => {}, // noop
    setIsInView: () => {}, // noop
});

export function AppProvider({ children }: { children: React.ReactNode }) {
    const [cursorState, setCursorState] = useState<CursorState>("default");
    const [cursorText, setCursorText] = useState<string>("");
    const [cursorCoordinates, setCursorCoordinates] = useState<CursorCoordinates>({ x: 0, y: 0 });
    const [isBannerHidden, setIsBannerHidden] = useState<boolean>(false);
    const [bannerBgColor, setBannerBgColor] = useState<string>("");
    const [isInView, setIsInView] = useState<boolean>(false);

    return (
        <AppContext.Provider
            value={{
                cursorState,
                cursorCoordinates,
                cursorText,
                isBannerHidden,
                bannerBgColor,
                isInView,
                setIsInView,
                setCursorState,
                setCursorText,
                setCursorCoordinates,
                setIsBannerHidden,
                setBannerBgColor,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useAppContext() {
    return useContext(AppContext);
}
